import { useMutation } from '@tanstack/react-query'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { toast } from 'react-hot-toast'

import { apiBaseUrl } from '~/configs/app'
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '~/constants/messages'
import pickup from '~/services/pickup'
import ticket from '~/services/ticket'
import type { GetTicketReqData, SocketReqSendQueue, SocketResSendQueue } from '~/types/ticket'
import { getBrowserName, isMobile } from '~/utils/userAgent'

import useDebugger from './useDebugger'
import useHandleError from './useHandleError'
import useSocket from './useSocket'

interface UseRequestTicketProps {
  locationId: string
  necessaryId: string
  kodeUnik?: string
}

// Shared by ModalPhone (self-pickup/batch) and the combined Surat Kuasa
// signature step: turns a WhatsApp number into a printed queue ticket -
// recaptcha, queue-request, PDF download, socket broadcast to the counter
// screen, then (if this came from a kode_unik flow) redeem-code.
const useRequestTicket = ({ locationId, necessaryId, kodeUnik }: UseRequestTicketProps) => {
  const { debug } = useDebugger()
  const handleError = useHandleError()
  const socket = useSocket()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const userBrowser = getBrowserName()
  const userDevice = isMobile() ? 'mobile' : 'web'

  const redeemCodeMutation = useMutation({
    mutationFn: pickup.redeemCode,
    onError: handleError,
  })

  const sendNewQueue = (reqData: SocketReqSendQueue) => {
    const { room, queue_id, queue_no, queue_secret_code, created_at, queue_necessary, is_kode_unik } = reqData
    debug('info', 'Socket `send_new_queue` request:', reqData)

    if (socket) {
      socket.emit(
        'send_new_queue',
        {
          room,
          payload: {
            queue_id,
            queue_no,
            queue_secret_code,
            created_at,
            queue_necessary,
            is_kode_unik,
          },
        },
        (response: SocketResSendQueue) => {
          debug('info', 'Socket `send_new_queue` response:', response)
        },
      )
    }
  }

  const requestTicketMutation = useMutation({
    mutationFn: ticket.getTicket,
    onError: handleError,
    onSuccess: (data) => {
      const {
        location_id,
        counter_code,
        queue_id,
        queue_no,
        queue_secret_code,
        created_at,
        queue_customer_phone,
        queue_necessary,
        is_kode_unik,
      } = data.data
      window.open(`${apiBaseUrl}/download?tiket=${queue_no}&phone=${queue_customer_phone}`)

      sendNewQueue({
        room: location_id + counter_code,
        queue_id,
        queue_no,
        queue_secret_code,
        created_at,
        queue_necessary,
        is_kode_unik,
      })

      if (kodeUnik) {
        redeemCodeMutation.mutate({
          kode_unik: kodeUnik,
          queue_id,
          queue_no,
          antrian_location_id: locationId,
          antrian_necessary_id: necessaryId,
        })
      }

      toast.success(SUCCESS_MESSAGES.PRINT_TICKET)

      debug('info', 'API `queue-request` response:', data)
    },
  })

  // Throws on any failure (missing recaptcha, no token, or the mutation's own
  // rejection) so callers can gate their "success" follow-up (closing the
  // modal, moving to the next step) on requestTicket actually succeeding -
  // errors are already toasted here/by the mutation's onError, so callers
  // just need an empty catch to stay put.
  const requestTicket = async (phone: string) => {
    if (!executeRecaptcha) {
      throw new Error('reCAPTCHA not ready')
    }
    const token = await executeRecaptcha('login')
    if (!token) {
      toast.error(ERROR_MESSAGES.NO_TOKEN)
      throw new Error('No reCAPTCHA token')
    }
    const formData: GetTicketReqData = {
      necessary_id: necessaryId,
      location_id: locationId,
      queue_customer_phone: phone,
      queue_origin: userDevice,
      queue_device_id: userBrowser,
      ...(kodeUnik && { queue_kode_unik: kodeUnik }),
      'g-recaptcha-response': token,
    }
    debug('info', 'API `queue-request` request:', formData)
    await requestTicketMutation.mutateAsync(formData)
  }

  return { requestTicket, isLoading: requestTicketMutation.isLoading }
}

export default useRequestTicket
